import React, { useEffect, useState, useCallback } from 'react';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SelectProps } from 'antd/es/select';
import type { PaginatedData, UseListParams } from '../../types/pagination.types';

type SelectValue = string | number;

interface SelectOption {
  label: string;
  value: SelectValue;
}

export interface SelectFieldProps<T> {
  value?: SelectValue;
  onChange?: (value: SelectValue) => void;
  useListHook: (params: UseListParams) => {
    data?: PaginatedData<T>;
    isLoading: boolean;
    isFetching: boolean;
  };
  labelKey: keyof T;
  valueKey: keyof T;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  required?: boolean;
  errorMsg?: string;
  searchParams?: Omit<UseListParams, 'page' | 'limit' | 'searchString'>;
  selectProps?: Omit<SelectProps, 'options' | 'loading' | 'value' | 'onChange'>;
  pageSize?: number;
}

const SelectField = <T extends Record<string, any>>({
  value,
  onChange,
  useListHook,
  labelKey,
  valueKey,
  placeholder,
  className,
  disabled = false,
  allowClear = true,
  required = false,
  errorMsg,
  searchParams = {},
  selectProps,
  pageSize = 10,
}: SelectFieldProps<T>) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [allOptions, setAllOptions] = useState<SelectOption[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [processedValues] = useState(new Set<string>());

  // Debounce search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Reset pagination when search changes
  useEffect(() => {
    setPage(1);
    setAllOptions([]);
    setHasMore(true);
    processedValues.clear();
  }, [debouncedSearch]);

  const { data, isLoading, isFetching } = useListHook({
    ...searchParams,
    searchString: debouncedSearch,
    page,
    limit: pageSize,
  });

  // Update options when new data arrives
  useEffect(() => {
    if (data?.docs) {
      const newOptions = data.docs
        .filter((item: T) => {
          const value = String(item[valueKey]);
          if (processedValues.has(value)) {
            return false;
          }
          processedValues.add(value);
          return true;
        })
        .map((item: T) => ({
          label: String(item[labelKey]),
          value: String(item[valueKey]),
        }));

      if (page === 1) {
        setAllOptions(newOptions);
      } else {
        setAllOptions((prev) => [...prev, ...newOptions]);
      }

      setHasMore(data.hasNextPage);
    }
  }, [data, labelKey, valueKey, page, processedValues]);

  const handleSearch = (newValue: string) => {
    setSearchValue(newValue);
  };

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e;
      if (!target) return;

      const scrollElement = target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;

      // Load more when user scrolls to bottom
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && !isLoading && hasMore) {
        setPage((prev) => prev + 1);
      }
    },
    [isLoading, hasMore]
  );

  return (
    <div className={className}>
      <Select
        showSearch
        value={value}
        placeholder={placeholder || t('common.select.placeholder')}
        defaultActiveFirstOption={false}
        filterOption={false}
        onSearch={handleSearch}
        onChange={onChange}
        notFoundContent={isLoading ? <Spin size="small" /> : null}
        options={allOptions}
        loading={isFetching}
        disabled={disabled}
        allowClear={allowClear}
        status={errorMsg ? 'error' : undefined}
        onPopupScroll={handlePopupScroll}
        listHeight={256}
        virtual
        style={{ width: '100%' }}
        {...selectProps}
      />
      {errorMsg && <div className="ant-form-item-explain-error">{errorMsg}</div>}
    </div>
  );
};

export default SelectField;
